import { createContext, useContext, useEffect, useRef, useState, type RefObject } from 'react'
import { Coins } from 'lucide-react'
import { Link } from '../router.js'
import {
  commeReponseCaisse, etatAffichage, jourMoisCourt, ligneCaisse, LecturesCaisseParPlage, nuitDeLaSoiree,
  phraseAvantCouverture, textesCaisse,
  type EmplacementCaisse, type NuitCaisse, type PlageCaisse, type ReponseCaisse,
} from './caisse-nuit-model.js'

/**
 * SOIRÉES — LA CAISSE DE LA NUIT, À L'ÉCRAN (15/10/2026).
 *
 * Les chiffres comptables de la nuit — Rapports, Compté, CA arrêté — posés
 * en SECOND PLAN dans les Soirées : petits, discrets, jamais en concurrence
 * avec les billets scannés. Les montants ne viennent que du serveur
 * (`GET /api/v1/soirees/night-cash`), calculés comme la fiche de nuit ; sans
 * le droit, ou l'emplacement éteint dans les Réglages, la réponse est vide
 * et RIEN ne s'affiche — pas même une phrase.
 *
 * Une lecture par écran : une soirée (la fiche) ou une plage (cartes,
 * calendrier) — l'historique, lui, une par MOIS quand il paraît à l'écran
 * (`useCaisseParMois`). Jamais une lecture par carte.
 */

/** Lit la caisse d'un emplacement ; `null` tant que rien n'est à montrer (chargement, refus, éteint). */
export function useCaisseNuit(
  emplacement: EmplacementCaisse,
  requete: { soiree: string } | { from: string; to: string } | null,
): ReponseCaisse | null {
  const [r, setR] = useState<ReponseCaisse | null>(null)
  const cle = requete === null ? '' : 'soiree' in requete ? `soiree=${requete.soiree}` : `from=${requete.from}&to=${requete.to}`
  useEffect(() => {
    // La réponse d'une AUTRE requête ne reste jamais affichée pendant que la
    // nouvelle se lit : la page d'une soirée n'est pas remontée quand on passe
    // à une autre, et la réponse de la précédente ne la nomme pas.
    setR(null)
    if (cle === '') return
    let vivant = true
    const arret = new AbortController()
    void fetch(`/api/v1/soirees/night-cash?placement=${emplacement}&${cle}`, { credentials: 'same-origin', signal: arret.signal })
      .then((x) => (x.ok ? x.json() : null))
      .then((j: unknown) => {
        const lu = commeReponseCaisse(j)
        if (vivant) setR(lu?.visible ? lu : null)
      })
      .catch(() => { if (vivant) setR(null) })
    return () => { vivant = false; arret.abort() }
  }, [emplacement, cle])
  return r
}

async function lireCaisse(emplacement: EmplacementCaisse, p: PlageCaisse, signal: AbortSignal): Promise<ReponseCaisse | null> {
  const x = await fetch(`/api/v1/soirees/night-cash?placement=${emplacement}&from=${p.from}&to=${p.to}`, { credentials: 'same-origin', signal })
  return x.ok ? commeReponseCaisse(await x.json()) : null
}

/**
 * L'historique, mois par mois (voir `LecturesCaisseParPlage`) : chaque mois
 * demande sa plage quand il paraît à l'écran (`useDemandeQuandVisible`), une
 * seule fois pour la vie de la page, un mois après l'autre.
 */
export function useCaisseParMois(emplacement: EmplacementCaisse): Pick<LecturesCaisseParPlage, 'reponse' | 'demander'> {
  const [, rendre] = useState(0)
  const ref = useRef<{ lectures: LecturesCaisseParPlage; arret: AbortController } | null>(null)
  if (ref.current === null) {
    const arret = new AbortController()
    ref.current = {
      arret,
      lectures: new LecturesCaisseParPlage((p) => lireCaisse(emplacement, p, arret.signal), () => rendre((n) => n + 1)),
    }
  }
  useEffect(() => () => { ref.current?.lectures.arreter(); ref.current?.arret.abort() }, [])
  const l = ref.current.lectures
  return { reponse: (p) => l.reponse(p), demander: (p) => l.demander(p) }
}

/**
 * Appelle `demander` quand l'élément paraît à l'écran (ou s'en approche) —
 * un mois replié dans une saison fermée ne paraît pas, et ne coûte rien.
 */
export function useDemandeQuandVisible(ref: RefObject<Element | null>, demander: () => void): void {
  const derniere = useRef(demander)
  derniere.current = demander
  useEffect(() => {
    const el = ref.current
    if (el === null) return
    if (typeof IntersectionObserver === 'undefined') { derniere.current(); return }
    const io = new IntersectionObserver((vus) => {
      if (vus.some((v) => v.isIntersecting)) { derniere.current(); io.disconnect() }
    }, { rootMargin: '200px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [ref])
}

/**
 * La ligne discrète des listes et des cartes : « Rapports 26 317 € · Compté
 * 26 278 € », « Rapports 33 803 € · Pas encore compté »…, précédée de « nuit du 03/10 · 2 soirées » quand la nuit en
 * porte plusieurs. Rien pour une nuit en cours, à venir, ou d'avant la
 * première trace de caisse.
 */
export function LigneCaisseNuit({ r, soireeId, className }: { r: ReponseCaisse | null; soireeId: string; className?: string }) {
  const n = nuitDeLaSoiree(r, soireeId)
  if (r === null || n === null || etatAffichage(r, n) !== 'chiffres') return null
  const t = textesCaisse(r, n)
  return (
    <span className={`so-caisse${className ? ` ${className}` : ''}`} title={t.aide}>
      {t.nuitPartagee ? <span className="so-caisse__nuit">{t.nuitPartagee} · </span> : null}
      {ligneCaisse(t)}
    </span>
  )
}

/* ═══════════════════════ la tuile de la fiche d'une soirée ═══════════════════════ */

/**
 * La page lit la caisse UNE fois (elle décide aussi si la tuile existe) et la
 * tend à la tuile par ce contexte : la tuile ne relance aucune lecture.
 */
export const CaisseSoireeContexte = createContext<{ r: ReponseCaisse | null; soireeId: string | null }>({ r: null, soireeId: null })

export function SoireeCaisseWidget() {
  const { r, soireeId } = useContext(CaisseSoireeContexte)
  const n: NuitCaisse | null = soireeId ? nuitDeLaSoiree(r, soireeId) : null
  if (r === null || n === null) {
    // En édition de la page seulement : la tuile existe, mais rien ne la remplit pour ce compte.
    return (
      <section className="lc-panel gd-panel so-widget" aria-label="Caisse de la nuit">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><Coins size={16} aria-hidden="true" /> Caisse de la nuit</h2>
        </header>
        <div className="lc-panel__body"><p className="lc-hint">Rien à afficher ici pour l’instant.</p></div>
      </section>
    )
  }
  const etat = etatAffichage(r, n)
  const t = textesCaisse(r, n, { auCentime: true })
  return (
    <section className="lc-panel gd-panel so-widget so-caisse-tuile" aria-label="Caisse de la nuit">
      <header className="lc-panel__head">
        <h2 className="lc-section-title"><Coins size={16} aria-hidden="true" /> Caisse de la nuit</h2>
        <span className="lc-hint">{t.nuitPartagee ?? `nuit du ${jourMoisCourt(n.business_date)}`}</span>
      </header>
      <div className="lc-panel__body">
        {etat === 'avant' ? (
          <p className="lc-hint">{phraseAvantCouverture(r.coverage_from)}</p>
        ) : (
          <>
            {/* `dt`/`dd` en enfants DIRECTS de `.lc-kv` : enveloppés d'un <div>,
                chaque paire devenait UNE cellule du gabarit — « Compté » à côté de
                « Rapports », et le montant cassé en plein nombre (« 26 317,0 / 0 € »). */}
            <dl className="lc-kv so-caisse-tuile__chiffres">
              <dt>Rapports</dt><dd title={n.rapport_help}>{t.rapports}</dd>
              <dt>Compté</dt><dd title={n.compte_help}>{t.compte}</dd>
              {t.caArrete !== null ? (
                <><dt>{t.caLibelle}</dt><dd title={n.ca_arrete_help ?? undefined}>{t.caArrete}</dd></>
              ) : null}
            </dl>
            <p className="lc-hint so-caisse-tuile__aide">
              {t.nuitPartagee
                ? `Chiffres de la nuit entière, communs à ses ${n.soirees.filter((s) => s.status !== 'CANCELLED').length} soirées. `
                : 'Chiffres de la nuit entière. '}
              {n.compte_cents === null ? n.rapport_help : n.compte_help}.
            </p>
          </>
        )}
        <p className="lc-hint">
          <Link href={`/comptabilite/nuits/${n.business_date}`}>Fiche de la nuit</Link>
        </p>
      </div>
    </section>
  )
}
